import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';

import {Headline, P} from 'UI/components/layout/Text';

/**
 * The props for the `DescriptionCard` component.
 * Configures a floating white card that overlaps the hero section with a subtitle and description.
 */
interface ComponentProps {
    /** The body text / longer description. */
    body: string;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'DescriptionCard'
     */
    testId?: string;
    /** The card headline (e.g. A narrative subtitle). */
    title: string;
}

/**
 * A floating white card that visually overlaps the hero, presenting
 * a narrative subtitle and the walk description text.
 * Matches the Stitch design's "Description Card" with surface-container-lowest background.
 *
 * @param props        The component props.
 * @param props.body   The descriptive body text.
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'DescriptionCard'`.
 * @param props.title  The headline text for the card.
 * @returns A JSX element rendering the DescriptionCard.
 *
 * @example
 * ```tsx
 * <DescriptionCard
 *     title="The Secret of the Weaver's Guild"
 *     body="Beneath the cobblestones..."
 * />
 * ```
 */
const DescriptionCard = ({body, testId = 'DescriptionCard', title}: ComponentProps) => (
    <Card data-cy={testId}>
        <CardTitle $size="headline" as="h2" data-cy={`${testId}-title`}>{title}</CardTitle>
        <CardBody $size="body" data-cy={`${testId}-body`}>{body}</CardBody>
    </Card>
);

DescriptionCard.displayName = 'DescriptionCard';

export {DescriptionCard};

// ── Styled ──────────────────────────────────────────────────

const Card = styled.section`
    backdrop-filter: blur(10px);
    background: #ffffffa9;
    border-radius: ${spacing(5)};
    box-shadow: ${({theme}) => theme.boxShadows.cardShadow};
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
    margin: ${spacing(-12)} ${spacing(6)} 0;
    padding: ${spacing(6)};
    position: relative;
    z-index: 10;

    ${media('lg')} {
        margin: 0 ${spacing(6)};
    }
`;

const CardTitle = styled(Headline)`
    color: ${({theme}) => theme.colors.primaryDefault};
`;

const CardBody = styled(P)`
    color: ${({theme}) => theme.colors.inkSecondary};
    line-height: 1.7;
`;